import {
  forwardRef,
  startTransition,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  stripRedundantSectionHeadingFromBody,
  type ComplianceDocumentSectionRecord,
} from '@artem-lex/compliance-documents'
import { Card } from '@/components/ui/card'
import { DocumentMenuBar } from '@/components/document-menu-bar'
import { readEditorMarkdown } from '@/features/compliance-studio/shared/compliance-document/complianceDocumentEditorUtils'
import {
  RecyclabilityAssessmentDocumentCoverBlock,
  RECYCLABILITY_ASSESSMENT_DOCUMENT_COVER_EDITOR_ID,
} from './RecyclabilityAssessmentDocumentCoverBlock'
import {
  RecyclabilityAssessmentDocumentSectionBlock,
  type RecyclabilitySectionEditorRegistration,
} from './RecyclabilityAssessmentDocumentSectionBlock'

export type RecyclabilityAssessmentDocumentEditorHandle = {
  flushSections: () => ComplianceDocumentSectionRecord[]
  flushCoverMarkdown: () => string | null
}

type RecyclabilityAssessmentDocumentEditorProps = {
  sections: ComplianceDocumentSectionRecord[]
  contentSyncKey: number
  coverMarkdown?: string | null
  coverContentSyncKey?: number
}

export const RecyclabilityAssessmentDocumentEditor = forwardRef<
  RecyclabilityAssessmentDocumentEditorHandle,
  RecyclabilityAssessmentDocumentEditorProps
>(function RecyclabilityAssessmentDocumentEditor(
  {
    sections,
    contentSyncKey,
    coverMarkdown,
    coverContentSyncKey = 0,
  },
  ref
) {
  const editorRegistrationsRef = useRef<
    Map<string, RecyclabilitySectionEditorRegistration>
  >(new Map())
  const [toolbarSectionId, setToolbarSectionId] = useState<string | null>(null)

  const sortedSections = useMemo(
    () => [...sections].sort((left, right) => left.sortOrder - right.sortOrder),
    [sections]
  )

  const registerSectionEditor = useCallback(
    (registration: RecyclabilitySectionEditorRegistration) => {
      editorRegistrationsRef.current.set(registration.sectionId, registration)
    },
    []
  )

  const unregisterSectionEditor = useCallback((sectionId: string) => {
    editorRegistrationsRef.current.delete(sectionId)
    setToolbarSectionId((current) => (current === sectionId ? null : current))
  }, [])

  const handleSectionFocus = useCallback((sectionId: string) => {
    startTransition(() => {
      setToolbarSectionId(sectionId)
    })
  }, [])

  const handleSectionEditorReady = useCallback((sectionId: string) => {
    startTransition(() => {
      setToolbarSectionId((current) => current ?? sectionId)
    })
  }, [])

  const showCoverEditor =
    coverMarkdown != null && coverMarkdown.trim().length > 0

  const toolbarEditor = (() => {
    if (toolbarSectionId == null) {
      return null
    }
    const editor =
      editorRegistrationsRef.current.get(toolbarSectionId)?.editorRef.current ??
      null
    if (editor == null || editor.isDestroyed) {
      return null
    }
    return editor
  })()

  const flushSections = useCallback((): ComplianceDocumentSectionRecord[] => {
    return sortedSections.map((section) => {
      const registration = editorRegistrationsRef.current.get(section.sectionId)
      const editor = registration?.editorRef.current
      if (editor == null || editor.isDestroyed) {
        return section
      }
      const markdown = stripRedundantSectionHeadingFromBody(
        readEditorMarkdown(editor),
        section.sectionId,
        section.title
      )
      return { ...section, markdown }
    })
  }, [sortedSections])

  const flushCoverMarkdown = useCallback((): string | null => {
    if (!showCoverEditor) {
      return null
    }
    const registration = editorRegistrationsRef.current.get(
      RECYCLABILITY_ASSESSMENT_DOCUMENT_COVER_EDITOR_ID
    )
    const editor = registration?.editorRef.current
    if (editor == null || editor.isDestroyed) {
      return coverMarkdown ?? null
    }
    return readEditorMarkdown(editor).trim()
  }, [coverMarkdown, showCoverEditor])

  useImperativeHandle(
    ref,
    () => ({ flushSections, flushCoverMarkdown }),
    [flushCoverMarkdown, flushSections]
  )

  return (
    <>
      {toolbarEditor != null ? (
        <div className='sticky top-0 z-10 -mx-6 mb-3 bg-background/95 px-6 backdrop-blur supports-backdrop-filter:bg-background/60'>
          <DocumentMenuBar editor={toolbarEditor} />
        </div>
      ) : null}
      <div className='flex flex-col gap-4'>
        {showCoverEditor ? (
          <Card>
            <RecyclabilityAssessmentDocumentCoverBlock
              markdown={coverMarkdown}
              contentSyncKey={coverContentSyncKey}
              onRegister={registerSectionEditor}
              onUnregister={unregisterSectionEditor}
              onSectionFocus={handleSectionFocus}
              onSectionEditorReady={handleSectionEditorReady}
            />
          </Card>
        ) : null}
        <Card>
          {sortedSections.map((section) => (
            <RecyclabilityAssessmentDocumentSectionBlock
              key={section.sectionId}
              section={section}
              sectionTitle={section.title}
              contentSyncKey={contentSyncKey}
              onRegister={registerSectionEditor}
              onUnregister={unregisterSectionEditor}
              onSectionFocus={handleSectionFocus}
              onSectionEditorReady={handleSectionEditorReady}
            />
          ))}
        </Card>
      </div>
    </>
  )
})
